import DatabaseModel from './DatabaseModel.js';

/**
 * Adapter OEB — todas las llamadas al backend del dominio OEB
 * (Organización Equipo de Bomberos / dashboard producción).
 *
 * El adapter NO envía el campo "usuario" en payloads de auditoría.
 * El backend usa SecurityUtils.getLoggedUsername() para todos los
 * campos creadoPor / modificadoPor (decalogo-ruben.md §2).
 *
 * Sólo se envía "usuario" cuando es filtro de negocio (ej. tpc-fechas
 * permite consultar el calendario de otro inspector).
 */

var Oeb = Object.create(DatabaseModel);

Oeb.model = 'oeb';

/**
 * Olvida lo cacheado del servicio OEB (60 s de sendRequest) después de una mutación. Sin esto la
 * recarga que viene detrás se serviría de la foto anterior, y Configuración OEB y Control de
 * cifras OEB — Planificación se presentan como "datos en vivo": enseñar lo de antes de guardar es
 * justo lo que #16 viene a evitar. Gemelo del de Oea.js.
 *
 * Ante un corte sin respuesta también se tira, con el mismo criterio que ya seguían a mano las
 * mutaciones de Productividad Planificación: el servidor puede haber aplicado el cambio, y si no
 * se tira la caché la relectura devuelve durante 60 s lo de antes bajo el sello "datos en vivo".
 * El error se vuelve a lanzar tal cual, que quien llamó tiene que saber que no se confirmó.
 */
function invalidarTrasMutacion(promesa) {
    return promesa.then(function(res) {
        Oeb.resetCache();
        return res;
    }).catch(function(err) {
        if (Oeb.esResultadoIncierto(err)) Oeb.resetCache();
        throw err;
    });
}

// ============================================================
// Filtros de negocio (NO auditoría — usuario legítimo del cliente)
// ============================================================

Oeb.tpcFechas = function(data) {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/tpc-fechas',
        method: 'POST',
        data: data
    }, false);
};

// ============================================================
// Ficha mensual y producción
// ============================================================

/**
 * Ficha de Producción OEB del mes, tal como la sirve oeb_produccion_cache.
 *
 * La ficha lleva `cifrasTpcCargadasEn` (#26): cuándo entraron por última vez las cifras TPC, que
 * NO es lo mismo que el `calculadoEn` de la caché —esa se rehace sola de noche y con "Recalcular",
 * y puede publicar cifras de hace días—. Control de cifras OEB enseña las dos fechas. Es null en
 * las fichas anteriores a #26, que no se rellenan hacia atrás.
 *
 * @param {number} anio
 * @param {number} mes
 * @returns {Promise<Object>} ResponseDto<{ficha: OebFichaMensualMO, calculadoEn: Date, ...}>
 */
Oeb.getFichaProduccion = function(anio, mes) {
    var self = this;
    var url = self.server_movilidad + self.prefix + self.model + '/produccion/ficha/' + anio + '/' + mes;
    return self.sendRequest({ url: url, method: 'GET' }, true);
};

// [#164] «Recalcular» rehace la ficha en el servidor, así que la copia cacheada de
// getFichaProduccion es de antes: sin invalidar, salir del mes y volver dentro del minuto siguiente
// devuelve las cifras anteriores al recálculo bajo el sello de «datos en vivo». El resto de
// recálculos del adapter (recalcularFicha, actualizarCifras) ya invalidaban; a este se le olvidó.
Oeb.recalcularProduccion = function(anio, mes) {
    var self = this;
    return invalidarTrasMutacion(self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/produccion/ficha/' + anio + '/' + mes + '/recalcular',
        method: 'POST',
        data: {}
    }, false));
};

Oeb.crearFicha = function(anio, mes) {
    var self = this;
    return invalidarTrasMutacion(self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/ficha/crear',
        method: 'POST',
        data: { anio: anio, mes: mes }
    }, false));
};

Oeb.recalcularFicha = function(anio, mes) {
    var self = this;
    return invalidarTrasMutacion(self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/recalcular-ficha/' + anio + '/' + mes,
        method: 'POST',
        data: {}
    }, false));
};

Oeb.actualizarCifras = function(anio, mes) {
    var self = this;
    return invalidarTrasMutacion(self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/actualizar-cifras',
        method: 'POST',
        data: { anio: anio, mes: mes }
    }, false));
};

// ============================================================
// Personas (configuración y reparto)
// ============================================================

Oeb.getPersonasReparto = function(anio, mes) {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/personas-reparto/' + anio + '/' + mes,
        method: 'GET'
    }, true);
};

Oeb.getPersonasConfig = function() {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/personas',
        method: 'GET'
    }, true);
};

Oeb.crearPersona = function(datos) {
    var self = this;
    return invalidarTrasMutacion(self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/personas',
        method: 'POST',
        data: datos
    }, false));
};

Oeb.getPersona = function(codigo) {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/personas/' + codigo,
        method: 'GET'
    }, true);
};

Oeb.eliminarPersona = function(codigo) {
    var self = this;
    return invalidarTrasMutacion(self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/personas/' + codigo,
        method: 'DELETE'
    }, false));
};

// ============================================================
// Personas dentro de una ficha (acciones operativas)
// ============================================================

/**
 * A quién puede añadir «+ Persona» en este mes (#85): las personas activas en la Configuración OEB
 * del AÑO de la ficha que todavía no se ven en el mes.
 *
 * El criterio lo decide el servidor. Antes cada pantalla lo montaba cruzando `getPersonasConfig()`
 * —la lista de `oeb_config_personas`, que no tiene año— con las filas que tenía pintadas, y así
 * personas activas en la configuración del ejercicio no llegaban a ofrecerse. Además «ya está en el
 * mes» depende de `personasVisibles`, que es de la ficha y la pantalla solo conoce de rebote.
 *
 * Sin la caché de peticiones (60 s): la lista cambia en cuanto alguien añade o quita a alguien del
 * mes, y quien abre el desplegable acaba de hacerlo.
 *
 * @param {string} fichaId
 * @returns {Promise<Object>} ResponseDto<Array<{codigoPersonal, nombre, clasificacion,
 *          objetivoDiarioActual, quitadaDelMes}>>
 */
Oeb.getPersonasDisponiblesParaFicha = function(fichaId) {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/ficha/' + fichaId + '/personas-disponibles',
        method: 'GET'
    }, false);
};

Oeb.agregarPersonaAFicha = function(fichaId, codigo) {
    var self = this;
    return invalidarTrasMutacion(self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/ficha/' + fichaId + '/persona/' + codigo,
        method: 'POST',
        data: {}
    }, false));
};

/**
 * Quita una persona de las pantallas OEB del mes. NO borra sus cifras (#34): deja de verse y,
 * si se la vuelve a añadir, sus datos del mes siguen donde estaban.
 */
Oeb.quitarPersonaDeFicha = function(fichaId, codigo) {
    var self = this;
    return invalidarTrasMutacion(self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/ficha/' + fichaId + '/persona/' + codigo,
        method: 'DELETE'
    }, false));
};

/**
 * Fija para el mes qué personas se ven en Control de cifras OEB y en OEB - Planificación (#34).
 * Las dos pantallas comparten ficha mensual, así que comparten lista. Es de la aplicación, no de
 * este usuario. Solo ADMIN y DIRECTOR; el backend lo comprueba igualmente.
 * @param {string} fichaId
 * @param {Array<string>} codigos codigoPersonal de las personas que quedan visibles
 */
Oeb.guardarPersonasVisibles = function(fichaId, codigos) {
    var self = this;
    return invalidarTrasMutacion(self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/ficha/' + fichaId + '/personas-visibles',
        method: 'PUT',
        data: { codigos: codigos }
    }, false));
};

// NOTA: el campo "usuario" NO se envía — el backend usa SecurityUtils
Oeb.cambiarObjetivoDiarioEnFicha = function(fichaId, codigo, datos) {
    var self = this;
    var payload = { objetivoDiario: datos.objetivoDiario };
    return invalidarTrasMutacion(self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/ficha/' + fichaId + '/persona/' + codigo + '/objetivo',
        method: 'PUT',
        data: payload
    }, false));
};

Oeb.modificarDiasHabiles = function(fichaId, codigo, datos) {
    var self = this;
    var payload = {
        semanaNumero: datos.semanaNumero,
        diasHabiles: datos.diasHabiles,
        motivo: datos.motivo
    };
    return invalidarTrasMutacion(self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/ficha/' + fichaId + '/persona/' + codigo + '/dias',
        method: 'PUT',
        data: payload
    }, false));
};

Oeb.cambiarClasificacionEnFicha = function(fichaId, codigo, datos) {
    var self = this;
    var payload = { clasificacion: datos.clasificacion };
    return invalidarTrasMutacion(self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/ficha/' + fichaId + '/persona/' + codigo + '/clasificacion',
        method: 'PUT',
        data: payload
    }, false));
};

// ============================================================
// Reportes por email
// ============================================================

Oeb.enviarReporte = function(datos) {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/enviar-reporte',
        method: 'POST',
        data: datos
    }, false);
};

// ============================================================
// Objetivos anuales
// ============================================================

/**
 * Objetivos anuales OEB. Sin la caché de peticiones (60 s), por lo mismo que su gemelo de Oea.js:
 * Configuración OEB lleva el sello "datos en vivo" (#16) y la editan varios administradores, de
 * modo que la invalidación tras mutación solo cubre los cambios de este navegador.
 */
Oeb.getObjetivosAnio = function(anio) {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/objetivos/' + anio,
        method: 'GET'
    }, false);
};

Oeb.guardarObjetivo = function(anio, codigo, datos) {
    var self = this;
    return invalidarTrasMutacion(self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/objetivos/' + anio + '/' + codigo,
        method: 'PUT',
        data: datos
    }, false));
};

Oeb.aplicarFactorObjetivos = function(anio, factor) {
    var self = this;
    return invalidarTrasMutacion(self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/objetivos/' + anio + '/aplicar-factor',
        method: 'POST',
        data: { factor: factor }
    }, false));
};

Oeb.copiarObjetivos = function(datos) {
    var self = this;
    return invalidarTrasMutacion(self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/objetivos/copiar',
        method: 'POST',
        data: datos
    }, false));
};

Oeb.migrarObjetivosDesdeConfig = function() {
    var self = this;
    return invalidarTrasMutacion(self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/objetivos/migrar-desde-config',
        method: 'POST',
        data: {}
    }, false));
};

// ============================================================
// Resumen anual
// ============================================================

/**
 * Resumen anual OEB de producción.
 *
 * Cada entrada de `totalesMes` lleva `cifrasTpcCargadasEn` (#26), igual que su gemelo de Oea.js:
 * cuándo entraron por última vez las cifras TPC en la ficha de ESE mes. La pantalla condensa las
 * doce en la más antigua. Es null en los meses sin ficha y en las fichas anteriores a #26.
 *
 * Sin la caché de peticiones (60 s), también como el gemelo: su botón "Actualizar" se limita a
 * llamar aquí, así que cacheado no traería nada nuevo durante el minuto siguiente.
 */
Oeb.getResumenAnual = function(anio) {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/resumen-anual/' + anio,
        method: 'GET'
    }, false);
};

// ============================================================
// Instrucciones (transversal — InfoIcon de cada pantalla OEB)
// ============================================================

Oeb.getInstrucciones = function(pantalla) {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/instrucciones/' + encodeURIComponent(pantalla),
        method: 'GET'
    }, true);
};

/**
 * Devuelve un único concepto por su clave estable. Útil para el componente
 * InfoIcon que carga conceptos puntuales por clave + pantalla.
 * @param {string} pantalla
 * @param {string} clave
 * @returns {Promise<Object>} ResponseDto<OebInstruccionMO>
 */
Oeb.getConcepto = function(pantalla, clave) {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/concepto/' + encodeURIComponent(pantalla) + '/' + encodeURIComponent(clave),
        method: 'GET'
    }, true);
};

// [#164] Las cinco mutaciones de instrucciones invalidan como el resto del adapter. Quien las
// llama recarga la lista justo después —al inicializar el set por defecto, y en el `catch` del
// reordenado para deshacer el movimiento que no cuajó—, y esa recarga pasa por getInstrucciones,
// que sí está cacheado: sin tirar la caché, la relectura devuelve la foto de antes y el `catch`
// restaura una lista anterior a cambios que sí se guardaron.
Oeb.inicializarInstrucciones = function(pantalla) {
    var self = this;
    return invalidarTrasMutacion(self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/instrucciones/inicializar/' + encodeURIComponent(pantalla),
        method: 'POST',
        data: {}
    }, false));
};

Oeb.crearInstruccion = function(datos) {
    var self = this;
    return invalidarTrasMutacion(self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/instrucciones',
        method: 'POST',
        data: datos
    }, false));
};

Oeb.actualizarInstruccion = function(id, datos) {
    var self = this;
    return invalidarTrasMutacion(self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/instrucciones/' + id,
        method: 'PUT',
        data: datos
    }, false));
};

Oeb.eliminarInstruccion = function(id) {
    var self = this;
    return invalidarTrasMutacion(self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/instrucciones/' + id,
        method: 'DELETE'
    }, false));
};

Oeb.reordenarInstrucciones = function(ids) {
    var self = this;
    return invalidarTrasMutacion(self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/instrucciones/orden',
        method: 'PUT',
        data: { ids: ids }
    }, false));
};

// ============================================================
// Planificación mensual
// ============================================================

/**
 * Ficha mensual de planificación OEB. Sin la caché de peticiones (60 s): la pantalla se presenta
 * como "datos en vivo" (#16) y la ficha la editan varios planificadores a la vez, así que la
 * invalidación tras mutación solo cubre los cambios de este navegador. Volver al mismo mes dentro
 * del minuto tiene que traer lo que haya guardado otro, no la foto de antes.
 *
 * @param incluirInfPend pedir también la vista INF.PEND. (#45): sus consultas son con diferencia
 *        las más caras de la pantalla y antes se pagaban siempre, aunque nadie estuviera mirando
 *        esa vista. Va explícito también cuando es `false`, porque la API lo deja encendido por
 *        defecto para no dejar sin columna a los navegadores que sigan con la versión anterior.
 */
Oeb.getFichaPlanificacion = function(anio, mes, incluirInfPend) {
    var self = this;
    var url = self.server_movilidad + self.prefix + self.model + '/planificacion/ficha/' + anio + '/' + mes;
    url += '?infPend=' + (incluirInfPend ? 'true' : 'false');
    return self.sendRequest({ url: url, method: 'GET' }, false);
};

/**
 * Solo la vista INF.PEND. del mes, para encender el botón con la ficha ya cargada sin volver a
 * pedirla entera (#45). Sin caché, por lo mismo que la ficha.
 */
Oeb.getInfPendPlanificacion = function(anio, mes) {
    var self = this;
    var url = self.server_movilidad + self.prefix + self.model + '/planificacion/inf-pend/' + anio + '/' + mes;
    return self.sendRequest({ url: url, method: 'GET' }, false);
};

/**
 * Guarda el numero de Interlocutores Comerciales (clientes/proveedores)
 * por persona para la planificacion mensual.
 */
Oeb.guardarNumerosIC = function(datos) {
    var self = this;
    return invalidarTrasMutacion(self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/num-ic',
        method: 'PUT',
        data: datos
    }, false));
};

/**
 * Preferencias de usuario por pantalla (toggles, etc.).
 * Backend identifica al usuario via SecurityUtils.getLoggedUsername().
 * GET devuelve { ... } o null si no hay registro previo.
 * PUT hace upsert con el body como Map<String,Object>.
 */
// Sin cache a proposito. Las preferencias se guardan solas al tocar la botonera, y la cache del
// modelo dura 60 s: con ella, salir y volver a entrar dentro de ese minuto devolvia la configuracion
// anterior y encima la pantalla la reenviaba al siguiente cambio, deshaciendo lo guardado. Es una
// lectura minuscula y una vez por carga; invalidar la cache entera del modelo saldria mas caro,
// porque ahi esta tambien la ficha del mes.
Oeb.getPreferencias = function(pantalla) {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/preferencias/' + encodeURIComponent(pantalla),
        method: 'GET'
    }, false);
};

Oeb.guardarPreferencias = function(pantalla, datos) {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/preferencias/' + encodeURIComponent(pantalla),
        method: 'PUT',
        data: datos
    }, false);
};

/**
 * Marca manualmente una semana del calendario OEB con un valor calculado a mano.
 * Endpoint PUT /oeb/manual delega en setManualOverride().
 * El motivo de la modificacion manual queda registrado para auditoria ENAC.
 */
Oeb.setManualOverride = function(datos) {
    var self = this;
    return invalidarTrasMutacion(self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/manual',
        method: 'PUT',
        data: datos
    }, false));
};

Oeb.getAnalisisDiferenciasPorPersona = function(anio, mes, codigo) {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/analisis/diferencias/' + anio + '/' + mes + '/' + codigo,
        method: 'GET'
    }, true);
};

// ============================================================
// Planificación diaria
// ============================================================

/**
 * Con forzarRecarga se salta la caché de peticiones. La pantalla de Planificación Diaria la pide
 * siempre forzada: se presenta como "datos en vivo" y el día lo tocan varios planificadores a la
 * vez, así que servir la respuesta de hasta 60 s antes sería enseñar la foto anterior bajo ese
 * sello. Eso cubre además el caso de la petición colgada: CacheService guarda su promesa pendiente
 * 60 s y devolvería esa misma, así que reintentar sin forzar es esperar otra vez a una petición
 * que ya no va a contestar.
 */
Oeb.getPlanificacionDiaria = function(anio, mes, dia, forzarRecarga) {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/planificacion-diaria/' + anio + '/' + mes + '/' + dia,
        method: 'GET'
    }, !forzarRecarga);
};

Oeb.getPlanificacionDiariaPdfDatos = function(anio, mes, dia) {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/planificacion-diaria/pdf-datos/' + anio + '/' + mes + '/' + dia,
        method: 'GET'
    }, true);
};

// ============================================================
// Productividad-Plan (productividad de planificadores)
// ============================================================

// forzarRecarga: ver getPlanificacionDiaria.
Oeb.getProductividadPlan = function(anio, mes, forzarRecarga) {
    var self = this;
    var url = self.server_movilidad + self.prefix + self.model + '/productividad-plan/' + anio + '/' + mes;
    return self.sendRequest({ url: url, method: 'GET' }, !forzarRecarga);
};

Oeb.recalcularProductividadPlan = function(anio, mes) {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/productividad-plan/' + anio + '/' + mes + '/recalcular',
        method: 'POST',
        data: {}
    }, false).then(function(res) {
        self.resetCache();
        return res;
    }).catch(function(err) {
        // Un corte sin respuesta no quiere decir que el recálculo no se haya hecho: el servidor
        // puede haberlo terminado igual. Si no se tira la caché, el refresco de después reutiliza
        // durante 60 s el GET anterior, la pantalla sigue enseñando las cifras viejas y quien mira
        // vuelve a pulsar Recalcular sin necesidad. Mismo criterio que la lista de ocultos.
        if (Oeb.esResultadoIncierto(err)) self.resetCache();
        throw err;
    });
};

Oeb.guardarConfigProductividadPlan = function(datos) {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/productividad-plan/config',
        method: 'PUT',
        data: datos
    }, false).then(function(res) {
        // Las GET de las dos pantallas se cachean 60 s en el navegador. Sin tirar esa caché,
        // Guardar y recargar dentro del minuto devuelve la config anterior y el %Obj recién
        // guardado no se ve — el mismo síntoma que se acaba de arreglar en el servidor.
        self.resetCache();
        return res;
    }).catch(function(err) {
        // Un corte sin respuesta tampoco quiere decir que no se haya guardado: el servidor puede
        // haber aplicado los %Obj y los toggles igualmente. Si no se tira la caché, la relectura
        // con la que se comprueba qué quedó devolvería durante 60 s la config anterior y taparía
        // justo el cambio que sí se aplicó. Mismo criterio que el recálculo y la lista de ocultos.
        if (Oeb.esResultadoIncierto(err)) self.resetCache();
        throw err;
    });
};

// Lista de planificadores ocultos del mes. Es de cada usuario, pero la MISMA para Productividad
// Planificación y Planificación Diaria, y por eso vive en el servidor y no en localStorage: si
// no, la misma persona veía una lista distinta en cada pantalla y en cada equipo.
// Endpoint propio: Diaria no edita %Obj ni toggles, y mandar la config entera los borraría.
//
// Se manda UNA persona por petición, nunca la lista entera: así un fallo solo deshace ese cambio
// y no arrastra a los demás.
Oeb.ocultarPersonaProductividadPlan = function(anio, mes, codigo) {
    return this.cambiarPersonaOcultaProductividadPlan(anio, mes, codigo, 'PUT');
};

Oeb.mostrarPersonaProductividadPlan = function(anio, mes, codigo) {
    return this.cambiarPersonaOcultaProductividadPlan(anio, mes, codigo, 'DELETE');
};

Oeb.cambiarPersonaOcultaProductividadPlan = function(anio, mes, codigo, metodo) {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/productividad-plan/' + anio + '/' + mes
            + '/personas-ocultas/' + encodeURIComponent(codigo),
        method: metodo
    }, false).then(function(res) {
        // Sin esto, refrescar o volver a una fecha consultada hace menos de 60 s devuelve la
        // respuesta cacheada, con la lista de ocultos anterior, y la persona que se acaba de
        // ocultar reaparece.
        self.resetCache();
        return res;
    }).catch(function(err) {
        // Un corte sin respuesta no quiere decir que el cambio no se haya aplicado, así que la
        // caché de 60 s tampoco vale: si no se tira, la relectura con la que se comprueba qué
        // quedó guardado devolvería la lista anterior y taparía la diferencia.
        if (Oeb.esResultadoIncierto(err)) self.resetCache();
        throw err;
    });
};

// La petición se cortó sin respuesta (timeout de pasarela o status 0). DatabaseModel.sendRequest
// lo trata aparte porque en estos estados el servidor puede haber aplicado la operación igualmente:
// quien llame no puede dar el cambio por fallido ni deshacerlo a ciegas, tiene que releer.
Oeb.esResultadoIncierto = function(err) {
    if (!err) return false;
    return err.status === 0 || [408, 504, 520, 522, 524, 598, 599].indexOf(err.status) !== -1;
};

Oeb.crearMesSiguienteProductividadPlan = function(datos) {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/productividad-plan/crear-mes-siguiente',
        method: 'POST',
        data: datos
    }, false);
};

Oeb.enviarReporteProductividadPlan = function(datos) {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/enviar-reporte-prod-plan',
        method: 'POST',
        data: datos
    }, false);
};

// ============================================================
// Productividad anual
// ============================================================

Oeb.getProductividadAnual = function(anio) {
    var self = this;
    var url = self.server_movilidad + self.prefix + self.model + '/productividad/anual/' + anio;
    return self.sendRequest({ url: url, method: 'GET' }, true);
};

Oeb.recalcularProductividadAnual = function(anio) {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/productividad/anual/' + anio + '/recalcular',
        method: 'POST',
        data: {}
    }, false);
};

Oeb.guardarConfigProductividad = function(datos) {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/productividad/config',
        method: 'PUT',
        data: datos
    }, false);
};

/**
 * Lectura de fichaje (entradas/salidas) de un trabajador.
 * Deuda D2.1: pendiente filtro horizontal por rol — un usuario podria
 * consultar fichajes de otros trabajadores. Diferido a PR especifica.
 */
Oeb.getFichaje = function(codigo, anio, mes) {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/productividad/fichaje/' + codigo + '/' + anio + '/' + mes,
        method: 'GET'
    }, true);
};

Oeb.getPersonasPlantilla = function(anio) {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/personas-plantilla?anio=' + anio,
        method: 'GET'
    }, true);
};

Oeb.actualizarPersonasPlantillaBatch = function(datos) {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/personas-plantilla/batch',
        method: 'PUT',
        data: datos
    }, false);
};

// ============================================================
// Análisis diferencias
// ============================================================

Oeb.getAnalisisDiferencias = function(anio, mes) {
    var self = this;
    var url = self.server_movilidad + self.prefix + self.model + '/analisis/diferencias/' + anio + '/' + mes;
    return self.sendRequest({ url: url, method: 'GET' }, true);
};

Oeb.recalcularAnalisisDiferencias = function(anio, mes) {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/analisis/diferencias/' + anio + '/' + mes + '/recalcular',
        method: 'POST',
        data: {}
    }, false);
};

// ============================================================
// Ejercicios y consultas guardadas
// ============================================================

Oeb.getEjerciciosCamposFiltro = function() {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/ejercicios/campos-filtro',
        method: 'GET'
    }, true);
};

Oeb.getEjerciciosValoresFiltro = function(campo) {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/ejercicios/valores-filtro/' + encodeURIComponent(campo),
        method: 'GET'
    }, true);
};

/**
 * Todas las listas de valores de la ventana de Filtros en una sola peticion (#43).
 *
 * Antes se pedia un campo por clic, y cada peticion volvia a recorrer la coleccion de reparto
 * entera para agrupar: ocho recorridos en serie por pasar la ventana. Cliente no viene aqui,
 * se busca escribiendo con buscarEjerciciosClientes.
 *
 * @returns {Promise<Object>} ResponseDto<{valores, buscarEnServidor, calculadoEn}>
 */
Oeb.getEjerciciosValoresFiltroTodos = function() {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/ejercicios/valores-filtro',
        method: 'GET'
    }, true);
};

/**
 * Busca clientes por codigo o nombre para el filtro Cliente (#43). Hacen falta al menos dos
 * letras; con menos el servidor devuelve lista vacia.
 * @param {string} texto
 * @param {number} [limite]
 * @returns {Promise<Object>} ResponseDto<List<{codigo, nombre}>>
 */
Oeb.buscarEjerciciosClientes = function(texto, limite) {
    var self = this;
    var url = self.server_movilidad + self.prefix + self.model
        + '/ejercicios/clientes?q=' + encodeURIComponent(texto || '');
    if (limite) url += '&limite=' + limite;
    return self.sendRequest({ url: url, method: 'GET' }, true);
};

/**
 * Nombre de unos codigos de cliente concretos (#43). Lo necesita la ventana al abrirse sobre una
 * consulta guardada: los codigos ya estan elegidos y sin esto se verian pelados.
 * @param {string[]} codigos
 * @returns {Promise<Object>} ResponseDto<List<{codigo, nombre}>>
 */
Oeb.getEjerciciosClientesPorCodigo = function(codigos) {
    var self = this;
    var lista = (codigos || []).map(function(c) { return encodeURIComponent(c); }).join(',');
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/ejercicios/clientes?codigos=' + lista,
        method: 'GET'
    }, true);
};

/**
 * Consulta/genera datos de ejercicios anuales con filtros.
 * Endpoint POST /oeb/ejercicios delega en service.generarEjercicios()
 * que devuelve datos cacheados o los recalcula segun el flag recalcular.
 * Body: { anioDesde, anioHasta, filtros, recalcular, soloAnioActual }
 */
Oeb.getEjercicios = function(datos) {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/ejercicios',
        method: 'POST',
        data: datos
    }, false);
};

/**
 * Lista las consultas guardadas. NO envia usuario en query — backend
 * filtra por usuario logueado via SecurityUtils.
 */
Oeb.getConsultas = function() {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/consultas',
        method: 'GET'
    }, true);
};

Oeb.getConsulta = function(id) {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/consultas/' + id,
        method: 'GET'
    }, true);
};

// [#164] Mismo olvido que las instrucciones, encontrado al barrer el adapter: Ejercicios recarga
// con getConsultas —cacheado— justo después de crear, renombrar, compartir o borrar una consulta.
Oeb.crearConsulta = function(datos) {
    var self = this;
    return invalidarTrasMutacion(self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/consultas',
        method: 'POST',
        data: datos
    }, false));
};

Oeb.actualizarConsulta = function(id, datos) {
    var self = this;
    return invalidarTrasMutacion(self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/consultas/' + id,
        method: 'PUT',
        data: datos
    }, false));
};

Oeb.eliminarConsulta = function(id) {
    var self = this;
    return invalidarTrasMutacion(self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/consultas/' + id,
        method: 'DELETE'
    }, false));
};

Oeb.reordenarConsultas = function(orden) {
    var self = this;
    return invalidarTrasMutacion(self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/consultas/orden',
        method: 'PUT',
        data: { orden: orden }
    }, false));
};

export default Oeb;
